import React from "react";
import PropTypes from "prop-types";
import { Badge, StyledLink } from "orcs-design-system";

import HeaderMetaItem from "src/commonComponents/ProfileHeader/components/HeaderMetaItem";
import {
  useAllocationConfig,
  useWorkspaceFeatureFlags,
} from "src/contexts/global/WorkspaceContext";
import { fullDisplayName } from "src/util/personName";
import {
  formatPositionBadgeLabel,
  getPositionEntityLabel,
  getPositionId,
  getPositionPerson,
} from "src/util/position";
import { positionDetailsPath } from "src/util/routing";

/**
 * Badge / link for a filled position seat — uses workspace vacantRoleLabel
 * (e.g. "Position: Name" or "Vacant role: Name").
 */
const PositionBadgeLink = ({ position }) => {
  const { vacantRoleLabel } = useAllocationConfig();
  const positionPerson = getPositionPerson(position);
  const positionName = positionPerson ? fullDisplayName(positionPerson) : null;
  const positionId = getPositionId(position);
  const label = formatPositionBadgeLabel(
    vacantRoleLabel,
    positionName,
    positionId
  );
  const badge = <Badge variant="primary">{label}</Badge>;

  if (!positionPerson?.aggregateId) {
    return badge;
  }

  return (
    <StyledLink
      to={positionDetailsPath({
        id: positionId || positionPerson.aggregateId,
      })}
      aria-label={label}
    >
      {badge}
    </StyledLink>
  );
};

PositionBadgeLink.propTypes = {
  position: PropTypes.shape({
    id: PropTypes.string,
    person: PropTypes.shape({
      aggregateId: PropTypes.string,
      preferredName: PropTypes.string,
      firstName: PropTypes.string,
      surname: PropTypes.string,
    }),
    aggregateId: PropTypes.string,
    preferredName: PropTypes.string,
    firstName: PropTypes.string,
    surname: PropTypes.string,
  }),
};

/**
 * Plain type meta for an isVacancy person (no name suffix).
 *
 * Gated here rather than at the call sites: legacy vacancies exist in every
 * workspace, so an isVacancy-only check would show position vocabulary to
 * customers who do not have the feature. Some callers (e.g.
 * PersonFormatOptionLabel) are plain formatter functions and cannot read the
 * flag themselves.
 */
export const PositionTypeBadge = () => {
  const { vacantRoleLabel } = useAllocationConfig();
  const { enablePositionBackedVacancies } = useWorkspaceFeatureFlags();

  if (!enablePositionBackedVacancies) {
    return null;
  }

  return (
    <HeaderMetaItem
      icon={["far", "id-badge"]}
      label={getPositionEntityLabel(vacantRoleLabel)}
      popoverContent="This is a position that can be filled by a person"
      data-testid="position-type-meta"
    />
  );
};

export default PositionBadgeLink;
